// Combobox: type a few characters, pick from what matches. The kit well with a chevron that says
// "there is a list", the shared ComboboxList under it, and the combobox pattern a screen reader
// expects (role, expanded, active descendant). Over a known set it filters as you type; over a
// server search (`filterOptions={false}`, `onQueryChange`) the options are the answer.
// docs/operations/design-standard.md, "Controls"; docs/features/shared-combobox.md.
//
// Blur closes on a 120ms timer, not a focus-within check: mousedown on a row fires before blur,
// so the timer only has to outlive that gap, and every alternative broke selection on Safari.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ComboboxList, ComboboxOption, useDropSide } from './ComboboxList';
import { fieldClass, FieldSize } from './Field';
import { IconChevronDown } from './icons';

export type { ComboboxOption } from './ComboboxList';

// React 17 has no useId; these ids never need to match a server render.
let seq = 0;

type NativeInput = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'onSelect' | 'size' | 'type' | 'className'>;

export interface ComboboxProps extends NativeInput {
  options: ComboboxOption[];
  /** The chosen option's id; '' or null for none. */
  value: string | number | null;
  onSelect: (option: ComboboxOption | null) => void;
  /** What is being chosen. The field's accessible name; a visible label comes from FieldRow. */
  label: string;
  size?: FieldSize;
  /** 40px under a coarse pointer, for a phone at the bench. */
  thumb?: boolean;
  error?: boolean;
  warn?: boolean;
  /** An icon inside the left edge: a magnifier on a people search, a plus on an add-to-list picker. */
  leading?: React.ReactNode;
  /** The chevron at the right edge. Default on; off for a search that is not a choice from a set. */
  chevron?: boolean;
  /**
   * Whether focus alone opens the list. Default on. Off where the field is focused at mount for
   * rapid entry, so the list does not cover what sits below it before anyone asked. Typing, the
   * down arrow and a click still open it.
   */
  openOnFocus?: boolean;
  /** Characters needed before any row shows. 0 shows the whole set at rest. */
  minQuery?: number;
  /** In the list's place when a query matches nothing. */
  emptyMessage?: React.ReactNode;
  /** In the list's place when there are no options at all. */
  noOptionsMessage?: React.ReactNode;
  /** Match the sublabel and chip too, not only the label. Default on. */
  searchSublabel?: boolean;
  /** Filter the options by the query. Off when the options already are a server's answer. */
  filterOptions?: boolean;
  /** The query on every keystroke, and '' when it clears (Escape, a pick, the blur). */
  onQueryChange?: (query: string) => void;
  /** A search is in flight: the list shows the shape of rows until results land. */
  busy?: boolean;
  /** How a row with a sublabel reads. `inline` puts it after the label (a surname). */
  layout?: 'stacked' | 'inline';
  /** Width and layout, on the box that holds the field and its list. */
  className?: string;
  /**
   * @deprecated For EntityCombobox's callers only, until each moves onto the kit field: the
   * input takes exactly these classes and draws no icon or chevron.
   */
  legacyFieldClass?: string;
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(({
  options,
  value,
  onSelect,
  label,
  size = 'md',
  thumb,
  error,
  warn,
  leading,
  chevron = true,
  openOnFocus = true,
  minQuery = 0,
  emptyMessage,
  noOptionsMessage,
  searchSublabel = true,
  filterOptions = true,
  onQueryChange,
  busy,
  layout = 'stacked',
  className = '',
  legacyFieldClass,
  id,
  disabled,
  placeholder = 'Type to search',
  onFocus,
  onBlur,
  onKeyDown,
  onClick,
  ...rest
}, ref) => {
  const [made] = useState(() => `ui-combo-${++seq}`);
  const fieldId = id || made;
  const listId = `${fieldId}-list`;

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)) || null,
    [options, value],
  );

  // `query` is what was typed; at rest the field shows the chosen label instead. Keeping them
  // apart is what lets the field read "1900 Opdyke" and still filter from scratch on a keystroke.
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  // The only writer of the query, so an async caller hears every reset and drops stale results.
  const queryRef = useRef('');
  const changeQuery = (next: string) => {
    if (queryRef.current === next) return;
    queryRef.current = next;
    setQuery(next);
    if (onQueryChange) onQueryChange(next);
  };

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (typing ? q.length < minQuery : minQuery > 0) return [];
    // Pinned rows come out first and go back last, so they survive the filter and end the list.
    const pinned = options.filter((o) => o.pinned);
    const rows = pinned.length ? options.filter((o) => !o.pinned) : options;
    const matched = !filterOptions || !typing || !q
      ? rows
      : rows.filter((o) => o.label.toLowerCase().includes(q)
        || (searchSublabel && ((o.sublabel || '').toLowerCase().includes(q) || (o.chip || '').toLowerCase().includes(q))));
    return pinned.length ? [...matched, ...pinned] : matched;
  }, [options, q, typing, minQuery, searchSublabel, filterOptions]);

  // Keep the keyboard inside a list that shrinks under typing, or the next Enter takes nothing.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const side = useDropSide(wrap, open);
  const showList = open && filtered.length > 0;
  const below = typing ? q.length < minQuery : minQuery > 0;
  const message = below ? undefined : options.length === 0 ? noOptionsMessage : emptyMessage;
  const drawn = open && !disabled && (filtered.length > 0 || !!busy || !!message);

  const commit = (option: ComboboxOption) => {
    onSelect(option);
    changeQuery('');
    setTyping(false);
    setOpen(false);
  };

  const reset = () => {
    setOpen(false);
    setTyping(false);
    changeQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const n = filtered.length;
      if (n) setActive((a) => (e.key === 'ArrowDown' ? (a + 1) % n : (a - 1 + n) % n));
    } else if (e.key === 'Enter') {
      // Owned only while the list is open with a row under the keyboard; a page that also uses
      // Enter reads defaultPrevented.
      if (open && filtered[active]) { e.preventDefault(); commit(filtered[active]); }
    } else if (e.key === 'Escape' && open) {
      // Swallowed only when it closed something: a drawer above wants Escape once the list is shut.
      e.stopPropagation();
      reset();
    }
    if (onKeyDown) onKeyDown(e);
  };

  const legacy = legacyFieldClass !== undefined;
  const pad = legacy ? '' : `${leading ? (size === 'sm' ? 'pl-7' : size === 'lg' ? 'pl-10' : 'pl-8') : ''} ${chevron ? 'pr-8' : ''}`;
  const inputClass = legacy ? legacyFieldClass : `${fieldClass({ size, error, warn, thumb })} ${pad}`;

  return (
    <div ref={wrap} className={`relative min-w-0 ${className}`}>
      {!legacy && leading && (
        <span className={`pointer-events-none absolute inset-y-0 flex items-center text-on-surface-variant ${size === 'sm' ? 'left-2' : size === 'lg' ? 'left-3' : 'left-2.5'}`}>
          {leading}
        </span>
      )}
      <input
        ref={ref}
        id={fieldId}
        className={inputClass}
        value={typing ? query : selected?.label || ''}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-label={label}
        aria-invalid={error || undefined}
        aria-busy={busy || undefined}
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${listId}-${active}` : undefined}
        {...rest}
        onChange={(e) => {
          changeQuery(e.target.value);
          setTyping(true);
          setOpen(true);
          setActive(0);
        }}
        onFocus={(e) => {
          // A blur still counting down must not wipe what is typed after focus comes back: the
          // receive drawer bounces focus through this field as it opens.
          if (blurTimer.current) { clearTimeout(blurTimer.current); blurTimer.current = null; }
          if (openOnFocus) setOpen(true);
          if (onFocus) onFocus(e);
        }}
        onClick={(e) => {
          // A click is an ask, even on a field already focused with its list shut.
          if (!open) setOpen(true);
          if (onClick) onClick(e);
        }}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          blurTimer.current = setTimeout(reset, 120);
          if (onBlur) onBlur(e);
        }}
      />
      {!legacy && chevron && (
        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-on-surface-variant">
          <IconChevronDown className={`h-4 w-4 transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${drawn ? 'rotate-180' : ''}`} />
        </span>
      )}
      <ComboboxList
        id={listId}
        label={label}
        options={filtered}
        open={open && !disabled}
        active={active}
        onActive={setActive}
        onPick={commit}
        side={side}
        selectedId={selected ? selected.id : null}
        layout={layout}
        busy={busy && filtered.length === 0}
        message={message}
      />
    </div>
  );
});
Combobox.displayName = 'Combobox';

export default Combobox;
